import { ForecastMetadata, WeatherService } from "../services/weather-service";
import { HighlightPastTrailActions, PastTrailData, VoyagePastTrailModelBase, VoyagePastTrailModelEvents } from "./voyage-past-trail-model";
import { LocationAndTime } from "./weather-model";
import { weatherElements } from "./voyage-past-trail-model";
import { formatSpotWeatherElement } from "../utils/weather-utils";
import { CoordinatesUtils } from "../utils/coordinates-utils";
import { UiStateModel, UiStateModelParam } from "./ui-state-model";
import { VesselPositionData, VesselPositionDataExtended } from "../services/types/position-service.types";
import { VesselsTrackingModel } from "./vessels-tracking-model";
import { fixupInvalidPosValues } from "../utils/utils";

interface SpotWeatherElement {
    element: string; value: number[];
}

const tsToTime = (timestamp: string) => { return new Date(timestamp).getTime(); }

export class VoyagePastTrailModelImpl extends VoyagePastTrailModelBase {
    private _trackSegmentMap: Map<string, PastTrailData> | null= null;
    private _fromDate: number = 0;
    private _toDate: number = 0;
    private _hasWeather: boolean = false;
    private _loadingPastTrailPromise: Promise<void> | undefined = undefined;

    get trackSegment(): PastTrailData[] {
        if (this._trackSegmentMap) {
            return Array.from(this._trackSegmentMap!.values());
        }
        return [];
    }

    get loadingPastTrailPromise() {
        return this._loadingPastTrailPromise;
    }

    public static convertToPastTrailData (positionData: VesselPositionDataExtended): PastTrailData {
        let formattedSpeed = positionData.sog;
        let source;
        let formattedCoordinates;
        if (positionData.sog) {
            const speed = positionData.sog;
            formattedSpeed = (Math.round(speed * 10) / 10.); // round to one decimal
        }
        
        if (positionData.latitude && positionData.longitude) {
            formattedCoordinates = CoordinatesUtils.decimalToDegreesAndMinutes([positionData.longitude, positionData.latitude])
        }

        const pastTrailData: PastTrailData = {
            timestamp: positionData.timestamp,
            latitude: formattedCoordinates?.lat || null,
            longitude: formattedCoordinates?.lon || null,
            heading: positionData.heading,
            course: positionData.course,
            speed: formattedSpeed,
            source: positionData.source,
            positionData: positionData,
            navigationalStatus: positionData.navStatus
        }

        return pastTrailData;
    } 

    public highlightPastTrailPoint (wp:any, action:HighlightPastTrailActions){
        if(action===HighlightPastTrailActions.Focus) this.dispatchEvent(new CustomEvent(VoyagePastTrailModelEvents.RowFocused, { detail: wp }))
        else this.dispatchEvent(new CustomEvent(VoyagePastTrailModelEvents.RowSelected, { detail: wp }))
    }

    public highlightTableOnPastTrailClick(wp:any) {
        this.dispatchEvent(new CustomEvent(VoyagePastTrailModelEvents.PastTrailClicked, { detail: wp }))       
    }

    public hasWeather() {
        return this._hasWeather;
    }
    
    async createPastTrailSegment(imo: number, fromDate: number, toDate: number) {
        this._trackSegmentMap?.clear(); // clear track, so it is not shown on map / in graph when user clicks on a another vessel
        this._hasWeather = false;
        
        this._loadingPastTrailPromise = VesselsTrackingModel?.syncTracking(imo, new Date(fromDate), new Date(toDate));
        await this._loadingPastTrailPromise;

        const positions = VesselsTrackingModel!.getTracking(imo)?.positions;

        const selectedImo = UiStateModel!.getValue(UiStateModelParam.IMO);
        
        if (selectedImo && parseInt(selectedImo) !== imo) {
            return;
        }

        this._fromDate = fromDate;
        this._toDate = toDate;

        if (positions) {

            this._trackSegmentMap = new Map<string, PastTrailData>();

            positions.forEach(p => {
                fixupInvalidPosValues(p);
                const pastTrailData = VoyagePastTrailModelImpl.convertToPastTrailData(p);
                this._trackSegmentMap!.set(p.timestamp, pastTrailData);
            });

            this._loadWeather(positions, imo);
        } else {
            this._trackSegmentMap = null;
        }
        this.dispatchEvent(new CustomEvent(VoyagePastTrailModelEvents.TrackUpdated, { detail: { imo } }));
    }

    async fetchWeatherSinglePosition(position: PastTrailData): Promise<PastTrailData> {
        const spotWeather = await WeatherService.getSpotWeather([position.positionData.longitude!, position.positionData.latitude! ], 
                                                                 weatherElements, 
                                                                 position.positionData.timestamp, 
                                                                 position.weatherForecast!.name);
        // @ts-ignore
        this._assignWeatherToPosition(<SpotWeatherElement[]>spotWeather.locations[0].properties.weather, position);
        
        return position;
    }

    async fetchWeather() {
        const forecastBuckets: Map<ForecastMetadata, PastTrailData[]> = new Map<ForecastMetadata, PastTrailData[]>();
        this.trackSegment.forEach(position => {
            if (position.weatherForecast) {
                if (forecastBuckets.has(position.weatherForecast)) {
                    forecastBuckets.get(position.weatherForecast)!.push(position);
                } else {
                    forecastBuckets.set(position.weatherForecast, [position]);
                }
            }
        });

        for (let [forecast, positions] of forecastBuckets) {
            const locations: LocationAndTime[] = positions.map(p => {return { coordinates: [ p.positionData.longitude!, p.positionData.latitude! ], timestamp: p.timestamp}});
            const multiSpotWeather = await WeatherService.getMultiSpotWeather(locations, weatherElements, forecast.name);
            
            multiSpotWeather.forEach(sw => {
                const pastTrailData = this._trackSegmentMap!.get(sw.properties.timestamp!);
                this._assignWeatherToPosition(<SpotWeatherElement[]>sw.properties.weather, pastTrailData!);
            })
        }
        this._hasWeather = true;
        this.dispatchEvent(new Event(VoyagePastTrailModelEvents.TrackUpdated));
    }

    private async _loadWeather(positions: VesselPositionDataExtended[], imo: number) {
        // DTN bug: missing the forecast that occurs on the requested fromDate, which means a bunch of positions will be without forecast.
        // So until DTN fixes this issue, we fetch 6 hours further back to make sure we get forecasts covering all positions. Should be removed
        // when bug in the weather API is fixed
        const deducted6Hours = 1000 * 60 * 60 * 6;
        const weatherForecasts = await WeatherService.getForecasts(new Date(this._fromDate - deducted6Hours), new Date(this._toDate));

        this._trackSegmentMap = new Map<string, PastTrailData>();

        positions.forEach(p => {
            const forecast = this._findWeatherForecast(tsToTime(p.timestamp), weatherForecasts!);
            const pastTrailData = VoyagePastTrailModelImpl.convertToPastTrailData(p);
            pastTrailData.weatherForecast = forecast;
            this._trackSegmentMap!.set(p.timestamp, pastTrailData);
        });

        this.dispatchEvent(new CustomEvent(VoyagePastTrailModelEvents.TrackUpdated, { detail: { imo } }));
    }

    private _findWeatherForecast(timestamp: number, weatherForecasts: ForecastMetadata[] ) {
        for (let i=0; i <= weatherForecasts.length-1; i++) {
            if (i === weatherForecasts.length-1 && timestamp >= tsToTime(weatherForecasts[i].timestamp)) {
                return weatherForecasts[i];
            }

            if (timestamp >= tsToTime(weatherForecasts[i].timestamp) && timestamp < tsToTime(weatherForecasts[i+1].timestamp)) {
                return weatherForecasts[i];
            }
        }
    }

    private _assignWeatherToPosition(spotWeather: SpotWeatherElement[], position: PastTrailData) {
        const spotWeatherMap = new Map(spotWeather.map(i => [i.element, i.value]));

        position!.wind50M = formatSpotWeatherElement("Wind50M", spotWeatherMap);
        position!.windGusts50M = formatSpotWeatherElement("WindGusts50M", spotWeatherMap);
        position!.riskWaveHeight = formatSpotWeatherElement("RiskWaveHeight", spotWeatherMap);
        position!.wave = formatSpotWeatherElement("Wave", spotWeatherMap);
        position!.swell = formatSpotWeatherElement("Swell", spotWeatherMap);
        position!.currents = formatSpotWeatherElement("Currents", spotWeatherMap);

    }
}
